/**
 * 管理模式切换组件
 * 显示在 Header 右侧，用于在业务模式和管理模式之间切换
 */

import React, { useMemo } from 'react';
import { Switch, Tooltip, Space, Tag, Badge, Popconfirm, Typography } from 'antd';
import {
  ShoppingOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useAdminMode } from '../../contexts/AdminModeContext';
import './index.less';

const { Text } = Typography;

interface AdminModeSwitchProps {
  /** 紧凑模式（移动端） */
  compact?: boolean;
  /** 主色调 */
  primaryColor?: string;
  /** 模式切换后的回调（已废弃，请使用 onEnterSuccess 和 onExitSuccess） */
  onModeChange?: (isAdminMode: boolean) => void;
  /** 进入管理模式成功后的回调 */
  onEnterSuccess?: () => void;
  /** 退出管理模式成功后的回调 */
  onExitSuccess?: () => void;
}

/**
 * 格式化剩余时间
 */
const formatRemainingTime = (seconds: number): string => {
  if (seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 获取时间状态颜色
 */
const getTimeStatusColor = (seconds: number): string => {
  if (seconds <= 60) return '#ff4d4f'; // 危险 - 1分钟内
  if (seconds <= 300) return '#faad14'; // 警告 - 5分钟内
  return '#52c41a'; // 正常
};

const AdminModeSwitch: React.FC<AdminModeSwitchProps> = ({
  compact = false,
  primaryColor = '#1890ff',
  onModeChange,
  onEnterSuccess,
  onExitSuccess,
}) => {
  const {
    isInAdminMode,
    isLoading,
    hasPermission,
    remainingSeconds,
    timeoutMinutes,
    enterAdminMode,
    exitAdminMode,
  } = useAdminMode();

  // 处理模式切换
  const handleModeChange = async (checked: boolean) => {
    let success = false;
    if (checked) {
      success = await enterAdminMode();
      if (success) {
        // 调用进入成功回调
        onEnterSuccess?.();
        // 兼容旧版 onModeChange
        onModeChange?.(true);
      }
    } else {
      success = await exitAdminMode('manual');
      if (success) {
        // 调用退出成功回调
        onExitSuccess?.();
        // 兼容旧版 onModeChange
        onModeChange?.(false);
      }
    }
  };

  // 计算时间状态
  const timeColor = useMemo(() => getTimeStatusColor(remainingSeconds), [remainingSeconds]);
  const formattedTime = useMemo(() => formatRemainingTime(remainingSeconds), [remainingSeconds]);

  // 没有权限时不显示
  if (!hasPermission) {
    return null;
  }

  // 紧凑模式（移动端）
  if (compact) {
    return (
      <div className="admin-mode-switch-compact">
        {isInAdminMode ? (
          <Popconfirm
            title="退出管理模式"
            description="确定要退出管理模式吗？"
            onConfirm={() => handleModeChange(false)}
            okText="确定"
            cancelText="取消"
          >
            <Badge dot color={timeColor}>
              <ToolOutlined style={{ color: primaryColor, fontSize: 18 }} />
            </Badge>
          </Popconfirm>
        ) : (
          <Popconfirm
            title="进入管理模式"
            description={`管理模式将在 ${timeoutMinutes} 分钟后自动退出`}
            onConfirm={() => handleModeChange(true)}
            okText="确定"
            cancelText="取消"
          >
            <ShoppingOutlined style={{ color: '#666', fontSize: 18 }} />
          </Popconfirm>
        )}
      </div>
    );
  }

  // 正常模式
  return (
    <div className="admin-mode-switch">
      <Tooltip
        title={
          isInAdminMode
            ? `管理模式 - 剩余 ${formattedTime}`
            : `切换到管理模式 (${timeoutMinutes}分钟超时)`
        }
      >
        <Space size={8} className="switch-container">
          {/* 业务模式图标 */}
          <ShoppingOutlined
            style={{
              color: !isInAdminMode ? primaryColor : '#999',
              transition: 'color 0.3s',
            }}
          />

          {/* 切换开关 */}
          <Switch
            checked={isInAdminMode}
            onChange={handleModeChange}
            loading={isLoading}
            size="small"
            checkedChildren={<ToolOutlined />}
            unCheckedChildren={<ShoppingOutlined />}
          />

          {/* 管理模式图标 */}
          <ToolOutlined
            style={{
              color: isInAdminMode ? primaryColor : '#999',
              transition: 'color 0.3s',
            }}
          />

          {/* 管理模式时显示倒计时 */}
          {isInAdminMode && (
            <Tag
              icon={<ClockCircleOutlined />}
              color={timeColor}
              className="countdown-tag"
            >
              {formattedTime}
            </Tag>
          )}
        </Space>
      </Tooltip>
    </div>
  );
};

export default AdminModeSwitch;
